import { useEffect, useState } from "react";

import { PalButton, PalSwitch } from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { QRCode } from "react-qrcode-logo";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Col, Label, Row } from "reactstrap";
import * as Yup from "yup";

import styled from "@emotion/styled";
import { Box } from "@mui/material";

import PRButton from "~components/Generic/PRButton";
import PRDivider from "~components/Generic/PRDivider";
import PRInput from "~components/Generic/PRInput";
import PRLink from "~components/Generic/PRLink";
import PRModal from "~components/Generic/PRModal";
import PRSelect from "~components/Generic/PRSelect";
import {
  notificationPreferenceType,
  notificationPreferenceTypeMap,
  userAuthenticationMethod,
  userAuthenticationMethodOptions,
  userRole,
} from "~constants";
import AlertHelper from "~helpers/AlertHelper";
import { useFirebaseWorkerExists } from "~helpers/NotificationHelper";
import Utils from "~helpers/Utils";
import { getCurrentUser, update2FAStatus, updateCurrentUser, updateCurrentUserPassword } from "~store/actions";
import {
  createNotificationPreference,
  deleteNotificationPreference,
  getNotificationPreferences,
  requestPushNotification,
  sendTestPushNotification,
} from "~store/notification/actions";
import { selectIsNotificationRequesting, selectNotificationPreferences } from "~store/notification/selectors";
import { selectCurrentProject, selectUserInfo } from "~store/user/selectors";

const StyledQrWrapper = styled.div`
  canvas {
    width: 100% !important;
    height: auto !important;
  }
`;

const QRCodeModal = withCardon(
  function QRCodeModalContent({ get, authenticationUrl, secretKey }) {
    const { t } = useTranslation();
    return (
      <PRModal size="sm" submitText={""} title="QR Code" onClick={get(true)} onClose={get(false)}>
        <Row>
          <Col lg={12}>
            <Alert color="info">
              <strong>{t("component.profileModal.qrCodeAlert")}</strong>
            </Alert>
          </Col>
          <Col lg={12}>
            <StyledQrWrapper>
              <QRCode logoPaddingStyle="square" logoWidth={50} quietZone={0} size={512} value={authenticationUrl} />
            </StyledQrWrapper>
          </Col>
          <Col className="text-center" lg={12}>
            <strong>{t("component.profileModal.otpKey")}: </strong> {secretKey}
          </Col>
        </Row>
      </PRModal>
    );
  },
  {
    destroyOnHide: true,
  }
);

const ProfileModal = withCardon(
  function ProfileModal({ get }) {
    const userInfo = useSelector(selectUserInfo);
    const dispatch = useDispatch();
    const [showDetail, setShowDetail] = useState(false);
    const [authenticationUrl, setAuthenticationUrl] = useState(null);
    const [secretKey, setSecretKey] = useState(null);
    const [showOtpEnter, setShowOtpEnter] = useState(false);
    const { t } = useTranslation();
    const notificationPreferences = useSelector(selectNotificationPreferences);
    const isNotificationRequesting = useSelector(selectIsNotificationRequesting);
    const currentProject = useSelector(selectCurrentProject);
    const firebaseWorkerExists = useFirebaseWorkerExists();

    useEffect(() => {
      dispatch(getNotificationPreferences());
    }, [dispatch]);
    const formik = useFormik({
      enableReinitialize: true,
      initialValues: {
        firstname: userInfo.firstname,
        lastname: userInfo.lastname,
        email: userInfo.email,
        preferred_2fa_method: userInfo.preferred_2fa_method || userAuthenticationMethod.email,
        otp_code: "",
        two_factor_enabled: userInfo.two_factor_enabled,
        // username: "",
        currentPassword: "",
        password: "",
        confirmPassword: "",
        notificationPreferences: notificationPreferences,
        // secret_question: "",
        // secret_answer: "",
      },
      validationSchema: Yup.object({
        firstname: Yup.string().required(t("component.profileModal.firstNameRequired")),
        lastname: Yup.string().required(t("component.profileModal.lastNameRequired")),
        email: Yup.string()
          .required(t("component.profileModal.emailRequired"))
          .test("email", t("component.profileModal.emailInvalid"), (value) => {
            return Utils.validateEmail(value);
          }),
        // username: Yup.string().required("Please Enter Your Username"),
        currentPassword: Yup.string().when({
          is: (val) => !!val?.length,
          then: Yup.string().required(t("component.profileModal.currentPasswordRequired")),
        }),
        password: Yup.string().when({
          is: (val) => !!val?.length,
          then: Yup.string()
            .required(t("component.profileModal.passwordRequired"))
            .min(8, t("component.profileModal.passwordMin"))
            .max(64, t("component.profileModal.passwordMax"))
            .test("passwordComplexity", t("component.profileModal.passwordComplexity"), (value) =>
              Utils.validatePassword(value)
            ),
        }),
        // secret_question: Yup.string().required("Please Enter Your Secret Question"),
        // secret_answer: Yup.string().required("Please Enter Your Secret Answer"),
        confirmPassword: Yup.string().when({
          is: (val) => !!val?.length,
          then: Yup.string()
            .required(t("component.profileModal.confirmPasswordRequired"))
            .oneOf([Yup.ref("password"), null], t("component.profileModal.passwordsMustMatch")),
        }),
        otp_code: Yup.string().when({
          is: () => showOtpEnter,
          then: Yup.string().required(t("component.profileModal.otpCodeRequired")),
        }),
      }),
      onSubmit: async (values) => {
        const payload = {
          data: values,
        };
        if (values.password == values.confirmPassword && values.currentPassword) {
          await dispatch(
            updateCurrentUserPassword({
              new_password: values.password,
              current_password: values.currentPassword,
            })
          );
        }
        let disableModalClose = false;
        if (
          values.two_factor_enabled !== userInfo.two_factor_enabled ||
          values.preferred_2fa_method !== userInfo.preferred_2fa_method
          //|| values.preferred_2fa_method === userAuthenticationMethod.authenticator
        ) {
          const preferredMethod = values.two_factor_enabled ? values.preferred_2fa_method : "none";
          const twoFAResponse = await dispatch(
            update2FAStatus({
              preferred_method: preferredMethod,
              ...(showOtpEnter && { otp_code: values.otp_code }),
            })
          );
          if (twoFAResponse.invalid_otp) {
            AlertHelper.show(t("component.profileModal.invalidOtp"), "error");
            return;
          } else if (twoFAResponse.otp_validation_required) {
            setShowOtpEnter(true);
            if (values.preferred_2fa_method === userAuthenticationMethod.authenticator) {
              const secretKey = twoFAResponse.secret_key;
              setSecretKey(secretKey);
              const urlTemplate = `otpauth://totp/Palmate:${userInfo.email}?secret=${secretKey}&issuer=Palmate`;
              setAuthenticationUrl(urlTemplate);
              disableModalClose = true;
            } else {
              AlertHelper.show(t("component.profileModal.otpCodeSent"), "success");
            }
            return;
          }

          delete payload.data.two_factor_enabled;
          delete payload.data.preferred_2fa_method;
        }
        delete payload.data.currentPassword;
        delete payload.data.password;
        delete payload.data.confirmPassword;
        await dispatch(updateCurrentUser(payload));
        await dispatch(getCurrentUser());
        if (!disableModalClose) {
          get(false)();
        }
      },
    });
    const handleToggleDetail = () => {
      setShowDetail((prev) => !prev);
    };

    const handleChangeAuthenticationMethod = (value) => {
      formik.setFieldValue("preferred_2fa_method", value);
    };

    const handleCheck2fa = (e) => {
      const checked = e.target.checked;
      if (checked && formik.values.preferred_2fa_method === "none") {
        formik.setFieldValue("preferred_2fa_method", userAuthenticationMethod.email);
      }
      formik.setFieldValue("two_factor_enabled", checked);
    };

    const handleClickShowQrCode = () => {
      QRCodeModal.show({ authenticationUrl, secretKey });
    };

    const handleChangeAddRemoveNotificationPreference = (preferenceKey) => async (e) => {
      const checked = e.target.checked;
      if (checked) {
        await dispatch(
          createNotificationPreference({
            notification_type: preferenceKey,
          })
        );
      } else {
        const notificationPreferenceObject = formik.values.notificationPreferences.find(
          (item) => item.notification_type === preferenceKey
        );
        if (notificationPreferenceObject?.id) {
          await dispatch(deleteNotificationPreference(notificationPreferenceObject.id));
        }
      }
      dispatch(getNotificationPreferences());
    };

    const handleClickSendTestPushNotification = async () => {
      dispatch(
        sendTestPushNotification({
          test: "true",
          body: t("component.profileModal.testNotificationBody"),
          title: t("component.profileModal.testNotificationTitle"),
        })
      );
    };
    const notificationCheckedAgentWaiting = formik.values.notificationPreferences.some(
      (item) => item.notification_type === notificationPreferenceType["Agent Waiting"]
    );
    const notificationCheckedMessages = formik.values.notificationPreferences.some(
      (item) => item.notification_type === notificationPreferenceType["Message"]
    );
    const isNotificationEnabledAndRegistered =
      window.Notification?.permission === "granted" && !!userInfo.device?.registration_id && firebaseWorkerExists;

    const livechatValidRoles = [userRole.admin, userRole.callCenterAgent];
    const hasLivechatAccess = livechatValidRoles.some((role) => currentProject?.permissions?.includes(role));

    return (
      <PRModal
        submitText={t("common.update")}
        title={t("component.profileModal.profileSettings")}
        onClick={formik.handleSubmit}
        onClose={get(false)}
      >
        {showOtpEnter && <Alert color="info">{t("component.profileModal.otpCodeAlert")}</Alert>}
        <Row>
          <Col lg={6}>
            <Label>{t("common.firstName")}</Label>
            <PRInput
              autoComplete="off"
              invalid={formik.touched.firstname && formik.errors.firstname}
              name="firstname"
              placeholder={t("component.profileModal.enterFirstName")}
              value={formik.values.firstname}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Col>
          <Col lg={6}>
            <Label>{t("common.lastName")}</Label>
            <PRInput
              autoComplete="off"
              invalid={formik.touched.lastname && formik.errors.lastname}
              name="lastname"
              placeholder={t("component.profileModal.enterLastName")}
              value={formik.values.lastname}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Col>
          <Col className="mt-3" xs={12}>
            <Label>{t("common.email")}</Label>
            <PRInput
              autoComplete="off"
              invalid={formik.touched.email && formik.errors.email}
              name="email"
              placeholder={t("component.profileModal.enterEmail")}
              value={formik.values.email}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Col>
          {hasLivechatAccess && (
            <Col className="mt-3" xs={12}>
              <Label> {t("component.profileModal.notifications")}</Label>

              {!isNotificationEnabledAndRegistered && (
                <Alert className="mb-2" color="info">
                  {t("component.profileModal.notificationPermission")}

                  <PRButton
                    className="ms-2"
                    color="primary"
                    disabled={isNotificationRequesting}
                    loading={isNotificationRequesting}
                    onClick={() => {
                      dispatch(requestPushNotification());
                    }}
                  >
                    {t("component.profileModal.allowNotification")}
                  </PRButton>
                </Alert>
              )}
              <Box alignItems={"center"} display={"flex"} gap={0.5}>
                <PalSwitch
                  checked={notificationCheckedAgentWaiting}
                  disabled={!isNotificationEnabledAndRegistered}
                  onChange={handleChangeAddRemoveNotificationPreference(notificationPreferenceType["Agent Waiting"])}
                />
                <Label size="md">{t(notificationPreferenceTypeMap[notificationPreferenceType["Agent Waiting"]])}</Label>
              </Box>
              <Box alignItems={"center"} display={"flex"} gap={0.5}>
                <PalSwitch
                  checked={notificationCheckedMessages}
                  disabled={!isNotificationEnabledAndRegistered}
                  onChange={handleChangeAddRemoveNotificationPreference(notificationPreferenceType.Message)}
                />
                <Label size="md">{t(notificationPreferenceTypeMap[notificationPreferenceType.Message])}</Label>
              </Box>

              <PalButton disabled={!isNotificationEnabledAndRegistered} onClick={handleClickSendTestPushNotification}>
                {t("component.profileModal.testNotification")}
              </PalButton>
            </Col>
          )}

          {userInfo.two_factor_enabled !== undefined && (
            <>
              <Col className="mt-3" xs={12}>
                <Label>{t("component.profileModal.enable2FA")}</Label>
                <Box>
                  <PalSwitch
                    checked={formik.values.two_factor_enabled}
                    disabled={formik.values.preferred_2fa_method === userAuthenticationMethod.none}
                    name="two_factor_enabled"
                    onBlur={formik.handleBlur}
                    onChange={handleCheck2fa}
                  />
                </Box>
              </Col>
              <Col className="mt-3" xs={12}>
                <Label>{t("component.profileModal.authenticationMethod")}</Label>
                <PRSelect
                  isPrimitiveValue
                  invalid={formik.touched.preferred_2fa_method && formik.errors.preferred_2fa_method}
                  isClearable={false}
                  isDisabled={!formik.values.two_factor_enabled || showOtpEnter}
                  name="preferred_2fa_method"
                  options={userAuthenticationMethodOptions}
                  placeholder={t("component.profileModal.select2FAAuthentication")}
                  value={formik.values.preferred_2fa_method}
                  onBlur={formik.handleBlur}
                  onChange={handleChangeAuthenticationMethod}
                />
              </Col>
            </>
          )}
          {showOtpEnter && (
            <>
              <Col className="mt-3" xs="12">
                <Label>{t("component.profileModal.otpCode")}</Label>
                <div className="d-flex justify-content-between gap-2">
                  <PRInput
                    autoComplete="off"
                    className="w-100"
                    invalid={formik.touched.otp_code && formik.errors.otp_code}
                    name="otp_code"
                    value={formik.values.otp_code}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                  {formik.values.preferred_2fa_method === userAuthenticationMethod.authenticator && (
                    <PRButton className="text-nowrap" style={{ height: "fit-content" }} onClick={handleClickShowQrCode}>
                      {t("component.profileModal.showQrCode")}
                    </PRButton>
                  )}
                </div>
              </Col>
            </>
          )}
          {!showDetail ? (
            <PRLink className="mt-3 font-size-12" onClick={handleToggleDetail}>
              {t("component.profileModal.changePassword")}
            </PRLink>
          ) : (
            <>
              <PRDivider className="mt-4" color={"secondary"} />
              <Col className="mt-3" xs={12}>
                <Label>{t("component.profileModal.currentPassword")}</Label>

                <PRInput
                  autoComplete="off"
                  invalid={formik.touched.currentPassword && formik.errors.currentPassword}
                  name="currentPassword"
                  placeholder={t("component.profileModal.enterCurrentPassword")}
                  type="password"
                  value={formik.values.currentPassword}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Col>
              <Col className="mt-3" xs={12}>
                <Label>{t("component.profileModal.newPassword")}</Label>
                <PRInput
                  autoComplete="off"
                  invalid={formik.touched.password && formik.errors.password}
                  name="password"
                  placeholder={t("component.profileModal.enterNewPassword")}
                  type="password"
                  value={formik.values.password}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Col>
              <Col className="mt-3" xs={12}>
                <Label>{t("component.profileModal.confirmPassword")}</Label>
                <PRInput
                  autoComplete="off"
                  invalid={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  name="confirmPassword"
                  placeholder={t("component.profileModal.enterConfirmPassword")}
                  type="password"
                  value={formik.values.confirmPassword}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Col>
            </>
          )}
        </Row>
      </PRModal>
    );
  },
  {
    destroyOnHide: true,
  }
);

export default ProfileModal;
